import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { professionalsAPI, categoriesAPI } from '../lib/api';
import { formatPrice, getCategoryImage } from '../lib/utils';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, MapPin, Star, Filter, X, Clock, DollarSign } from 'lucide-react';

const SearchPage = () => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [professionals, setProfessionals] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    query: searchParams.get('q') || '',
    category: searchParams.get('category') || '',
    trade: searchParams.get('trade') || '',
    city: searchParams.get('city') || '',
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoriesAPI.getAll();
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProfessionals = async () => {
      setLoading(true);
      try {
        const params = {};
        if (filters.category) params.category_key = filters.category;
        if (filters.trade) params.trade = filters.trade;
        if (filters.city) params.city = filters.city;

        const response = await professionalsAPI.search(params);
        let results = response.data;

        // Filter by search query (client-side)
        if (filters.query) {
          const query = filters.query.toLowerCase();
          results = results.filter(
            (p) =>
              p.trade.toLowerCase().includes(query) ||
              p.description.toLowerCase().includes(query) ||
              p.user_name.toLowerCase().includes(query) ||
              p.category_name.toLowerCase().includes(query)
          );
        }

        setProfessionals(results);
      } catch (error) {
        console.error('Error fetching professionals:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfessionals();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    // Update URL params
    const params = new URLSearchParams();
    if (newFilters.query) params.set('q', newFilters.query);
    if (newFilters.category) params.set('category', newFilters.category);
    if (newFilters.trade) params.set('trade', newFilters.trade);
    if (newFilters.city) params.set('city', newFilters.city);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({ query: '', category: '', trade: '', city: '' });
    setSearchParams({});
  };

  const availableTrades = filters.category && categories[filters.category]
    ? categories[filters.category].trades
    : [];

  const hasActiveFilters = filters.category || filters.trade || filters.city;

  return (
    <div className="min-h-screen" data-testid="search-page">
      {/* Hero Header */}
      <div className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h1 className="text-3xl md:text-4xl text-white mb-6" style={{ fontFamily: '"DM Serif Display", serif' }}>
            {t('findProfessional')}
          </h1>
          
          {/* Search Bar */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                type="text"
                placeholder={t('searchPlaceholder')}
                value={filters.query}
                onChange={(e) => handleFilterChange('query', e.target.value)}
                className="h-12 pl-12 pr-4 rounded-xl text-base bg-white border-0"
                data-testid="search-input"
              />
            </div>
            <div className="relative flex-1 sm:max-w-xs">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Gatineau, Outaouais..."
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
                className="h-12 pl-12 pr-4 rounded-xl text-base bg-white border-0"
                data-testid="city-input"
              />
            </div>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="h-12 px-4 rounded-xl bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
              data-testid="toggle-filters-btn"
            >
              <Filter className="h-5 w-5 mr-2" />
              {t('filters')}
              {hasActiveFilters && (
                <span className="ml-2 w-5 h-5 rounded-full bg-orange-600 text-xs flex items-center justify-center">
                  {[filters.category, filters.trade, filters.city].filter(Boolean).length}
                </span>
              )}
            </Button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 p-4 rounded-xl bg-white/10 backdrop-blur-sm animate-fade-in">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-white/80 text-sm mb-2 block">{t('category')}</Label>
                  <Select
                    value={filters.category}
                    onValueChange={(value) => {
                      handleFilterChange('category', value);
                      handleFilterChange('trade', '');
                    }}
                  >
                    <SelectTrigger className="h-10 bg-white border-0 rounded-lg" data-testid="category-select">
                      <SelectValue placeholder={t('allCategories')} />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(categories).map(([key, cat]) => (
                        <SelectItem key={key} value={key}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {availableTrades.length > 0 && (
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-white/80 text-sm mb-2 block">{t('trade')}</Label>
                    <Select
                      value={filters.trade}
                      onValueChange={(value) => handleFilterChange('trade', value)}
                    >
                      <SelectTrigger className="h-10 bg-white border-0 rounded-lg" data-testid="trade-select">
                        <SelectValue placeholder={t('allTrades')} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTrades.map((trade) => (
                          <SelectItem key={trade} value={trade}>
                            {trade}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {hasActiveFilters && (
                  <div className="flex items-end">
                    <Button
                      variant="ghost"
                      onClick={clearFilters}
                      className="text-white/70 hover:text-white hover:bg-white/10"
                      data-testid="clear-filters-btn"
                    >
                      <X className="h-4 w-4 mr-1" />
                      {t('clear')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results Section */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Results Count */}
        <div className="flex justify-between items-center mb-6">
          <p className="text-slate-600">
            <span className="font-semibold text-slate-900">{professionals.length}</span> {t('professionalsFound')}
          </p>
        </div>

        {/* Results Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-64 rounded-xl skeleton-loading"></div>
            ))}
          </div>
        ) : professionals.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-slate-100 flex items-center justify-center">
              <Search className="h-10 w-10 text-slate-400" />
            </div>
            <h3 className="text-xl text-slate-900 mb-2" style={{ fontFamily: '"DM Serif Display", serif' }}>
              {t('noResults')}
            </h3>
            <p className="text-slate-600 mb-6">
              {t('tryModifySearch')}
            </p>
            <Button onClick={clearFilters} variant="outline" className="rounded-full" data-testid="empty-clear-filters">
              {t('clearFilters')}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {professionals.map((pro, index) => (
              <Link
                key={pro.id}
                to={`/professional/${pro.id}`}
                className="block animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'backwards' }}
                data-testid={`professional-card-${pro.id}`}
              >
                <Card className="h-full border-slate-100 overflow-hidden card-hover">
                  <div className="h-32 relative">
                    <img
                      src={getCategoryImage(pro.category_key)}
                      alt={pro.trade}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                    <div className="absolute bottom-3 left-3">
                      <span className="px-3 py-1 rounded-full bg-white/90 text-sm font-medium text-slate-900">
                        {pro.trade}
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">
                      {pro.user_name}
                    </h3>
                    <p className="text-sm text-slate-600 mb-3 flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {pro.city}
                    </p>
                    <p className="text-sm text-slate-500 line-clamp-2 mb-4">
                      {pro.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 text-orange-500 fill-orange-500" />
                        <span className="font-medium text-slate-900">
                          {pro.average_rating > 0 ? pro.average_rating.toFixed(1) : t('new')}
                        </span>
                        {pro.total_reviews > 0 && (
                          <span className="text-slate-400 text-sm">({pro.total_reviews})</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-slate-900 font-semibold">
                        <DollarSign className="h-4 w-4" />
                        {pro.hourly_rate}$/h
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
